import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Lock, MessageCircle, Send, X } from "lucide-react";
import Logo from "./Logo";
import { startGoogleSignIn } from "./auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GREETING =
  "Hi! I'm the StartX AI assistant. Ask me anything about features, pricing, or the free trial.";

export default function ChatWidget({ user, authChecked }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", content: GREETING },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [model, setModel] = useState("gpt");
  const sessionRef = useRef(crypto.randomUUID());
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTo({ top: listRef.current.scrollHeight });
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [
      ...m,
      { role: "user", content: text },
      { role: "assistant", content: "" },
    ]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text, model }),
      });
      if (!res.ok) throw new Error("Request failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const snapshot = acc;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: snapshot };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Sorry, something went wrong. Please try again.",
        };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-500 shadow-[0_0_36px_rgba(45,212,191,0.45)] transition-[transform,box-shadow] duration-300 hover:scale-110 hover:shadow-[0_0_52px_rgba(45,212,191,0.6)]"
        data-testid="chat-widget-button"
        aria-label="Open AI chat"
      >
        {open ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" />
        )}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-24 right-6 z-[80] flex h-[480px] w-[min(92vw,380px)] flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#0A0C1F]/95 shadow-[0_32px_90px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
            data-testid="chat-panel"
          >
            <div className="flex items-center gap-3 border-b border-white/5 px-5 py-4">
              <Logo className="h-8 w-8" />
              <div className="flex-1">
                <p className="font-display text-sm font-semibold text-white">
                  StartX AI Assistant
                </p>
                <p className="flex items-center gap-1.5 text-[11px] text-slate-500">
                  <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Online — replies instantly
                </p>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-slate-500 transition-colors duration-300 hover:text-white"
                data-testid="chat-close-button"
                aria-label="Close chat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {authChecked && !user ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-4 px-8 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl border border-white/10 bg-white/5">
                  <Lock className="h-5 w-5 text-teal-300" />
                </span>
                <p className="text-sm leading-relaxed text-slate-400">
                  Sign in with Google to chat with the StartX AI assistant.
                </p>
                <button
                  onClick={startGoogleSignIn}
                  className="rounded-full bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-500 px-6 py-2.5 text-sm font-semibold text-white shadow-[0_0_24px_rgba(45,212,191,0.35)] transition-[transform,box-shadow] duration-300 hover:scale-[1.03]"
                  data-testid="chat-signin-button"
                >
                  Sign in with Google
                </button>
              </div>
            ) : (
              <>
                <div
                  ref={listRef}
                  className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
                  data-testid="chat-messages"
                >
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[82%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                      msg.role === "user"
                        ? "rounded-br-md bg-gradient-to-br from-blue-600 to-teal-500 text-white"
                        : "rounded-bl-md border border-white/5 bg-white/5 text-slate-200"
                    }`}
                  >
                    {msg.content ||
                      (busy && i === messages.length - 1 ? (
                        <span className="pulse-dot inline-block h-2 w-2 rounded-full bg-teal-400" />
                      ) : (
                        ""
                      ))}
                  </div>
                </div>
              ))}
            </div>

              <div
                className="flex items-center gap-1.5 border-t border-white/5 px-3 pt-2.5"
                data-testid="chat-model-toggle"
              >
                <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                  Model
                </span>
                {[
                  { id: "gpt", label: "GPT" },
                  { id: "gemini", label: "Gemini" },
                ].map((m) => (
                  <button
                    key={m.id}
                    onClick={() => setModel(m.id)}
                    className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors duration-300 ${
                      model === m.id
                        ? "bg-gradient-to-r from-blue-600 to-teal-500 text-white"
                        : "border border-white/10 bg-white/5 text-slate-400 hover:text-white"
                    }`}
                    data-testid={`chat-model-${m.id}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2 p-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask about StartX Powered…"
                className="flex-1 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white placeholder-slate-600 outline-none transition-colors duration-300 focus:border-teal-400/50"
                data-testid="chat-input"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-blue-600 to-teal-500 text-white transition-[transform,opacity] duration-300 hover:scale-105 disabled:opacity-40"
                data-testid="chat-send-button"
                aria-label="Send message"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
